import httpClient from "../lib/httpClient";

// Service for handling file uploads (similar to studio's file upload pattern)
export const fileUploadService = {
  // Upload thumbnail image and get base64 data for metadata
  uploadThumbnailImage: async (file: File): Promise<string> => {
    try {
      // Convert file to base64 (following studio pattern)
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          const base64WithoutPrefix = base64String.substring(base64String.indexOf(',') + 1);
          resolve(base64WithoutPrefix);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    } catch (error) {
      console.error('Error converting thumbnail to base64:', error);
      throw error;
    }
  },

  // Upload video file to Azure Blob Storage
  uploadVideoFile: async (
    file: File, 
    showId: string,
    onProgress?: (progress: number) => void
  ): Promise<string> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('showId', showId);
      formData.append('type', 'video_thumbnail');

      // Use XMLHttpRequest for progress tracking
      return new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        if (onProgress) {
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const progress = (event.loaded / event.total) * 100;
              onProgress(progress);
            }
          });
        }

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response.url || response.videoUrl);
            } catch (e) {
              reject(new Error('Invalid response format'));
            }
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        // Use your existing API endpoint pattern
        xhr.open('POST', `/api/studio/show/id/${showId}/upload-video-thumbnail`);
        
        // Add auth header if available
        const token = localStorage.getItem('token');
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        xhr.send(formData);
      });
    } catch (error) {
      console.error('Error uploading video file:', error);
      throw error;
    }
  },

  // Bulk upload video files
  uploadMultipleVideoFiles: async (
    files: File[],
    showId: string,
    onFileProgress?: (fileIndex: number, progress: number) => void,
    onFileComplete?: (fileIndex: number, url: string) => void
  ): Promise<string[]> => {
    const uploadPromises = files.map(async (file, index) => {
      try {
        const url = await fileUploadService.uploadVideoFile(
          file,
          showId,
          (progress) => onFileProgress?.(index, progress)
        );
        onFileComplete?.(index, url);
        return url;
      } catch (error) {
        console.error(`Failed to upload file ${file.name}:`, error);
        throw error;
      }
    });

    return Promise.all(uploadPromises);
  },

  // Delete video file from Azure Blob Storage
  deleteVideoFile: async (videoUrl: string, showId: string): Promise<void> => {
    try {
      await httpClient.delete(`/api/studio/show/id/${showId}/video-thumbnail`, {
        data: { videoUrl }
      });
    } catch (error) {
      console.error('Error deleting video file:', error);
      throw error;
    }
  },

  // Delete multiple video files
  deleteMultipleVideoFiles: async (videoUrls: string[], showId: string): Promise<void> => {
    try {
      await httpClient.delete(`/api/studio/show/id/${showId}/video-thumbnails`, {
        data: { videoUrls }
      });
    } catch (error) {
      console.error('Error deleting video files:', error);
      throw error;
    }
  },

  // Validate file before upload
  validateThumbnailFile: (file: File): { valid: boolean; error?: string } => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'File must be JPG, JPEG, or PNG format' };
    }

    return { valid: true };
  },

  // Validate video file before upload
  validateVideoFile: (file: File): { valid: boolean; error?: string } => {
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Video must be MP4, MOV, AVI, or WebM format' };
    }

    return { valid: true };
  },

  // Reel upload functionality (following studio pattern)
  getGeneratedUrlForReel: async (fileName: string): Promise<{ signedUrl: string }> => {
    try {
      return await httpClient.post('/api/studio/reel-upload/generated-url', { fileName });
    } catch (error) {
      console.error('Error getting generated URL for reel:', error);
      throw error;
    }
  },

  uploadFileToGeneratedUrl: async (
    signedUrl: string,
    file: File,
    headers: Record<string, string>,
    onProgress?: (progress: number) => void
  ): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            onProgress(progress);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', signedUrl);
      
      // Set custom headers
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.send(file);
    });
  },

  // Create reel upload job
  createReelUploadJob: async (jobData: {
    reelUrl: string;
    showId: string;
    episodeId: string;
    sequence: number;
    createdBy: string;
  }): Promise<{ jobId: string }> => {
    try {
      return await httpClient.post('/api/studio/reel-upload/job', jobData);
    } catch (error) {
      console.error('Error creating reel upload job:', error);
      throw error;
    }
  },

  // Get content type for file
  getContentType: (file: File): string => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    switch (fileExtension) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'webp':
        return 'image/webp';
      case 'mp4':
        return 'video/mp4';
      case 'mov':
        return 'video/quicktime';
      case 'avi':
        return 'video/x-msvideo';
      case 'webm':
        return 'video/webm';
      default:
        return file.type || 'application/octet-stream';
    }
  },

  // Get generated URL for bonus content files
  getGeneratedUrlForBonusContent: async (fileName: string, showId: string): Promise<{ signedUrl: string }> => {
    try {
      return await httpClient.post('/api/studio/bonus-content-upload/generated-url', { 
        fileName,
        showId 
      });
    } catch (error) {
      console.error('Error getting generated URL for bonus content:', error);
      throw error;
    }
  },

  // Upload bonus content file to Azure with proper content type
  uploadBonusContentFile: async (
    file: File,
    showId: string,
    onProgress?: (progress: number) => void
  ): Promise<string> => {
    try {
      // Get signed URL for upload
      const { signedUrl } = await fileUploadService.getGeneratedUrlForBonusContent(file.name, showId);
      
      // Get proper content type
      const contentType = fileUploadService.getContentType(file);
      
      // Upload file with proper headers
      await fileUploadService.uploadFileToGeneratedUrl(
        signedUrl,
        file,
        { 
          'Content-Type': contentType, 
          'x-ms-blob-type': 'BlockBlob' 
        },
        onProgress
      );
      
      // Return the URL (extract from signed URL or construct)
      const url = signedUrl.split('?')[0]; // Remove query parameters
      return url;
    } catch (error) {
      console.error('Error uploading bonus content file:', error);
      throw error;
    }
  },

  // Upload bonus content image and get base64 (fallback method)
  uploadBonusContentImageAsBase64: async (file: File): Promise<string> => {
    try {
      // Try Azure upload first
      const url = await fileUploadService.uploadBonusContentFile(file, 'temp-show-id');
      return url;
    } catch (error) {
      console.warn('Azure upload failed, falling back to base64:', error);
      // Fallback to base64 conversion
      return fileUploadService.uploadThumbnailImage(file);
    }
  }
};